import { WorkspaceTool } from "../../base/WorkspaceTool.js";
import { logger } from "../../../../../../core/logger.js";

/**
 * UploadFolderTool - Handles uploading an entire folder as a subfolder within the target directory
 * Creates a new folder with the source folder name and uploads all contents preserving structure
 */
export class UploadFolderTool extends WorkspaceTool {
  constructor() {
    super();
    this.toolName = 'uploadFolderTool';
    this.name = "uploadFolder";
    this.description = "Upload an entire folder as a subfolder within the target directory";
    this.parameters = {
      type: "object",
      properties: {
        folderName: {
          type: "string",
          description: "Name of the folder to create in target location"
        },
        files: {
          type: "array",
          description: "Array of file objects with relativePath and content",
          items: {
            type: "object",
            properties: {
              relativePath: { type: "string", description: "Relative path within the folder (e.g., 'subfolder/file.txt')" },
              content: { type: "string", description: "File content as string" },
              encoding: { type: "string", description: "Content encoding (utf8 or base64)" }
            },
            required: ["relativePath", "content"]
          }
        },
        targetPath: {
          type: "string",
          description: "Target directory path where the folder should be created"
        }
      },
      required: ["folderName", "files", "targetPath"]
    };
  }

  async execute({ folderName, files, targetPath }) {
    try {
      logger.log({
        tags: "workspace|tool|upload-folder",
        color1: "blue"
      }, `UploadFolderTool: Creating folder "${folderName}" with ${files.length} files in ${targetPath}`);

      const results = [];

      for (const file of files) {
        try {
          // Create the full target path: targetPath/folderName/relativePath
          const fullTargetPath = targetPath
            ? `${targetPath}/${folderName}/${file.relativePath}`
            : `${folderName}/${file.relativePath}`;

          // Use writeFileTool to save each file
          const writeResult = await this.toolSystem.writeFileTool.writeFile(
            fullTargetPath,
            file.content,
            false, // overwrite - don't overwrite existing files
            file.encoding || 'utf8',
            true // createDirectories - this will create the folder structure
          );

          if (writeResult.success) {
            results.push({
              file: file.relativePath,
              fullPath: fullTargetPath,
              success: true
            });
            logger.log({
              tags: "workspace|tool|upload-folder",
              color1: "green"
            }, `Uploaded: ${folderName}/${file.relativePath} → ${fullTargetPath}`);
          } else {
            results.push({
              file: file.relativePath,
              fullPath: fullTargetPath,
              success: false,
              error: writeResult.message || writeResult.error
            });
            logger.log({
              tags: "workspace|tool|upload-folder",
              color1: "red"
            }, `Failed to upload ${folderName}/${file.relativePath}: ${writeResult.message || writeResult.error}`);
          }
        } catch (fileError) {
          const fullTargetPath = targetPath
            ? `${targetPath}/${folderName}/${file.relativePath}`
            : `${folderName}/${file.relativePath}`;

          results.push({
            file: file.relativePath,
            fullPath: fullTargetPath,
            success: false,
            error: fileError.message
          });
          logger.log({
            tags: "workspace|tool|upload-folder",
            color1: "red"
          }, `Error uploading ${folderName}/${file.relativePath}: ${fileError.message}`);
        }
      }

      const successCount = results.filter(r => r.success).length;
      const failureCount = results.filter(r => !r.success).length;

      return {
        success: failureCount === 0,
        data: {
          folderName,
          targetPath,
          results,
          summary: {
            total: files.length,
            successful: successCount,
            failed: failureCount
          }
        },
        message: `Uploaded folder "${folderName}" with ${successCount} of ${files.length} files${failureCount > 0 ? ` (${failureCount} failed)` : ''}`
      };

    } catch (error) {
      logger.log({
        tags: "workspace|tool|upload-folder",
        color1: "red"
      }, `UploadFolderTool error: ${error.message}`);

      return {
        success: false,
        error: error.message,
        message: `Failed to upload folder "${folderName}"`
      };
    }
  }
}